import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosAuth from "../axiosAuth";
import { FILE_UPLOAD, SCHOOL } from "./apiRoutes";

export interface CreateSchoolPayload {
  schoolName: string;
  state: string;
  city: string;
  zip: string;
  geoRegionId?: string | null;
  phoneNumber: string;
  email: string;
  website?: string | null;
  logo?: File | null;
  address1: string;
  address2?: string | null;
  academicCollaboration: string;
  gradesSelected: string[];
  facultyDays: string[];
  noOfFacultyDays?: string | null;
  educatorType: string;
  maintenance: string;
  sku: string;
  noOfWorkstations: string;
  includesBooks: boolean;
  schoolTier: string;
  syllabus: string[];
  accountType: string;
  studentStrength?: number;
}

const uploadFileAndGetUuid = async (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("file_type", 'logo');
  const res = await axiosAuth.post(FILE_UPLOAD, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res?.data?.data?.attachment_id as string;
};

export const useCreateSchoolMutation = (options?: { onSuccess?: (data: any) => void }) => {
  return useMutation({
    mutationFn: async (data: CreateSchoolPayload) => {
      let logoUuid: string | undefined;
      if (data.logo instanceof File) {
        logoUuid = await uploadFileAndGetUuid(data.logo);
      }

      const { logo, includesBooks, academicCollaboration, ...rest } =
        data as any;

      const normalizedYearOfCollaboration =
        academicCollaboration ?? rest?.yearsOfCollaboration ?? null;

      // Remove possible yearsOfCollaboration duplicate from rest
      if (rest && typeof rest === "object" && "yearsOfCollaboration" in rest) {
        delete (rest as any).yearsOfCollaboration;
      }

      const bodyWithNulls = {
        ...rest,
        academicCollaboration: normalizedYearOfCollaboration,
        includesBooks: includesBooks === true ? "Yes" : "No",
        schoolLogo: logoUuid,
      } as Record<string, unknown>;

      const body = Object.fromEntries(
        Object.entries(bodyWithNulls).filter(([, v]) => v !== null && v !== undefined)
      );

      const response = await axiosAuth.post(SCHOOL, body);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success("School created successfully!");
      options?.onSuccess?.(data);
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.issues?.message ||error?.response?.data?.issues?.error || "Failed to create school";
      toast.error(message);
    },
  });
};


